import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ConsentService } from '../../services/ConsentService';
import { createPortal } from 'react-dom';

interface FooterProps {
  setCurrentPage: (page: string) => void;
}

export const Footer: React.FC<FooterProps> = ({ setCurrentPage }) => {
  const [showConsentModal, setShowConsentModal] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const rejectButtonRef = useRef<HTMLButtonElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  
  const handleManageCookies = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowConsentModal(true);
    // Track when user opens the cookie management modal
    ConsentService.trackConsentManagement('open');
  };
  
  const handleAcceptAll = () => {
    // Use 'manage_modal' as source for tracking
    ConsentService.acceptCookies('manage_modal');
    setShowConsentModal(false);
  };
  
  const handleRejectAll = () => {
    // Use 'manage_modal' as source for tracking
    ConsentService.rejectCookies('manage_modal');
    setShowConsentModal(false);
  };
  
  const handleCloseModal = () => {
    // Track when user closes the modal without making a choice
    ConsentService.trackConsentManagement('close');
    setShowConsentModal(false);
  };
  
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showConsentModal) {
        // Track when user closes with Escape key
        ConsentService.trackConsentManagement('close');
        setShowConsentModal(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showConsentModal]);
  
  // Focus management for the modal
  useEffect(() => {
    if (showConsentModal) {
      // Focus the close button when the modal opens
      closeButtonRef.current?.focus();
      
      // Save the previously focused element to restore focus when modal closes
      const previouslyFocused = document.activeElement as HTMLElement;
      
      return () => {
        // Restore focus when the modal closes
        previouslyFocused?.focus();
      };
    }
  }, [showConsentModal]);
  
  // Focus trap inside the modal
  useEffect(() => {
    if (!showConsentModal) return;
    
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      // Get all focusable elements inside the modal
      const focusableElements = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as NodeListOf<HTMLElement>;
      
      if (!focusableElements || focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      // If shift+tab and first element is focused, go to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } 
      // If tab and last element is focused, go to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [showConsentModal]);

  // Cookie Consent Modal component rendered through portal
  const CookieConsentModal = () => {
    if (!showConsentModal) return null;
    
    return createPortal(
      <>
        {/* Backdrop with higher z-index to ensure it appears above everything */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999]"
          onClick={handleCloseModal}
          aria-hidden="true"
        ></div>
        
        {/* Modal dialog */}
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
          ref={modalRef}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-lg w-full pointer-events-auto animate-fadeIn"
            aria-describedby="cookie-preferences-description"
            onClick={(e) => e.stopPropagation()}
            style={{
              animationDuration: '0.3s',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 id="cookie-preferences-title" className="text-lg font-semibold text-gray-900">Cookie Preferences</h3>
                <button 
                  ref={closeButtonRef}
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 rounded p-1"
                  aria-label="Close cookie preferences dialog"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              <div id="cookie-preferences-description" className="prose prose-sm text-gray-600 mb-5">
                <p>
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies for analytics and personalization purposes.
                </p>
                <p className="mb-1">
                  You can find more information about the cookies we use in our <Link to="/privacy/" className="text-orange-500 hover:text-orange-600">Privacy Policy</Link>.
                </p>
              </div>
              
              <div className="text-gray-700 mb-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center py-3 border-b border-gray-200">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Essential Cookies</h4>
                    <p className="text-xs text-gray-500 mt-1">Required for the website to function properly</p>
                  </div>
                  <div className="ml-4">
                    <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full font-medium">Always On</span>
                  </div>
                </div>
                
                <div className="flex items-center py-3">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800">Analytics Cookies</h4>
                    <p className="text-xs text-gray-500 mt-1">Help us understand how you interact with our website</p>
                  </div>
                  <div className="ml-4">
                    <span 
                      className={`px-2 py-1 text-xs rounded-full font-medium ${
                        ConsentService.hasAcceptedCookies() 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}
                      aria-live="polite"
                    >
                      {ConsentService.hasAcceptedCookies() ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  ref={rejectButtonRef}
                  onClick={handleRejectAll}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                  aria-label="Reject all cookies"
                >
                  Reject All
                </button>
                <button
                  ref={acceptButtonRef}
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
                  aria-label="Accept all cookies"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  };
  
  return (
    <>
      <footer className="bg-gray-800 text-gray-300 py-12 min-h-[300px] w-full overflow-visible">
        <div className="container mx-auto px-4 overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Sitemap */}
            <div className="space-y-3">
              <h3 className="font-semibold text-white mb-4">Sitemap</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li><Link to="/learn/" className="hover:text-white">Learn</Link></li>
                <li><Link to="/practice/" className="hover:text-white">Practice</Link></li>
                <li><Link to="/blog/" className="hover:text-white">Blog</Link></li>
                <li><Link to="/about/" className="hover:text-white">About</Link></li>
                <li><Link to="/support/" className="hover:text-white">Support</Link></li>
                <li><Link to="/sitemap-page/" className="hover:text-white">Full Sitemap</Link></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="space-y-3">
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy/" className="hover:text-white">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms/" className="hover:text-white">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/contact/" className="hover:text-white">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={handleManageCookies} 
                    className="hover:text-white cursor-pointer bg-transparent border-none p-0 text-gray-300"
                  >
                    Manage Cookies
                  </button>
                </li>
              </ul>
            </div>

            {/* Disclaimer */}
            <div className="space-y-3">
              <h3 className="font-semibold text-white mb-4">Disclaimer</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                The information provided on Cognitive Bias Lab is for educational purposes only and does not 
                constitute professional advice. Please note that Cognitive Bias Lab is not an accredited 
                educational institution nor a licensed provider. Any reliance on the material is at your own risk.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 border-t border-gray-700 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Cognitive Bias Lab. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Render the cookie consent modal through portal */}
      <CookieConsentModal />
    </>
  );
}; 